import {pickBy} from 'lodash';

import {JsonObject} from '../utilTypes';
import {
  getPublicQueueConfig,
  OrderNode,
  PUBLIC_JOBS_INFO,
  TEST_PUBLIC_JOBS_INFO,
} from './configuration';
import {WANDB_ENTITY_NAME, WANDB_JOBS_PROJECT_NAME} from './constants';

// check if a queue is a public queue
//
// accepts either a queue object or queueName + entityName
//
// overload signatures
export function isPublicQueue(queue: {
  name: string;
  entityName: string;
}): boolean;
export function isPublicQueue(queueName: string, entityName: string): boolean;

export function isPublicQueue(
  queueOrName: {name: string; entityName: string} | string,
  entityName?: string
): boolean {
  if (typeof queueOrName === 'string') {
    const config = getPublicQueueConfig(queueOrName);
    return config != null && config.entityName === entityName;
  }
  const config = getPublicQueueConfig(queueOrName.name);
  return config != null && config.entityName === queueOrName.entityName;
}

export const isWandbJobsProject = (
  entityName: string,
  projectName: string
): boolean => {
  return (
    entityName === WANDB_ENTITY_NAME && projectName === WANDB_JOBS_PROJECT_NAME
  );
};

const normalizePath = (path: string | string[]): string[] =>
  Array.isArray(path) ? path : [path];

const findOrderNodeAtPath = (
  nodes: OrderNode[],
  targetPath: string[]
): OrderNode | undefined => {
  if (targetPath.length === 0) {
    return undefined;
  }
  for (const node of nodes) {
    const nodePath = normalizePath(node.path);
    if (nodePath.length === 1 && nodePath[0] === targetPath[0]) {
      if (targetPath.length === 1) {
        return node;
      }

      if (node.nestedPaths) {
        return findOrderNodeAtPath(node.nestedPaths, targetPath.slice(1));
      }
    }
  }
  return undefined;
};

const reorderProps = (
  props: Record<string, any>,
  childOrder?: string[]
): Record<string, any> => {
  if (!props) {
    return props;
  }
  if (!childOrder || childOrder.length === 0) {
    return Object.keys(props).reduce((acc: Record<string, any>, k) => {
      acc[k] = props[k];
      return acc;
    }, {});
  }
  const result: Record<string, any> = {};
  const remaining = new Set(Object.keys(props));
  for (const name of childOrder) {
    const key = String(name);
    if (Object.prototype.hasOwnProperty.call(props, key)) {
      result[key] = props[key];
      remaining.delete(key);
    }
  }
  for (const key of remaining) {
    result[key] = props[key];
  }
  return result;
};

const getChildOrder = (
  orderSpec: OrderNode[],
  currentPath: string[]
): string[] | undefined => {
  // no nested paths
  if (currentPath.length === 0) {
    return orderSpec.map(n => normalizePath(n.path)[0]);
  }

  const orderNode = findOrderNodeAtPath(orderSpec, currentPath);
  return orderNode?.nestedPaths?.map(n => {
    const p = normalizePath(n.path);
    return p[p.length - 1];
  });
};

export const reorderSchemaNode = (
  orderSpec: OrderNode[],
  node: any,
  currentPath: string[]
): any => {
  if (!node || typeof node !== 'object') {
    return node;
  }
  if (
    node.type === 'object' &&
    node.properties &&
    typeof node.properties === 'object'
  ) {
    const childOrder = getChildOrder(orderSpec, currentPath);

    const children: Record<string, any> = {};
    for (const key of Object.keys(node.properties)) {
      children[key] = reorderSchemaNode(orderSpec, node.properties[key], [
        ...currentPath,
        key,
      ]);
    }
    const orderedChildren = reorderProps(children, childOrder);
    return {...node, properties: orderedChildren};
  }
  return node;
};

export const sortPublicJobSchemas = (
  jobName: string,
  schemas: JsonObject
): JsonObject => {
  const allPublicJobsInfo = {
    ...PUBLIC_JOBS_INFO,
    ...TEST_PUBLIC_JOBS_INFO,
  };

  const jobInfo = allPublicJobsInfo[jobName];
  if (!jobInfo) {
    return schemas;
  }
  const root = (schemas as any)['@wandb.config'];
  if (root && root.properties) {
    return {
      ...schemas,
      '@wandb.config': reorderSchemaNode(jobInfo.inputOrderSpec, root, []),
    };
  }
  return schemas;
};

export const CONDITIONAL_FIELDS = {
  HF_TOKEN: 'hf_token',
  SCORER_API_KEY: 'scorer_api_key',
} as const;

// These evals require the HF token to access gated datasets.
export const EVALS_REQUIRING_HF_TOKEN = new Set([
  'hle',
  'lingoly',
  'lingoly_too',
  'mask',
  'personality_TRAIT',
]);

// These evals use OpenAI models as default scorers/graders.
export const EVALS_REQUIRING_SCORER_API_KEY = new Set([
  'agentharm',
  'agentharm_benign',
  'healthbench',
  'healthbench_consensus',
  'healthbench_hard',
  'mask',
  'simpleqa',
  'sosbench',
  'xstest',
]);

export function shouldShowField(
  fieldKey: string,
  selectedTasks: string[]
): boolean {
  if (fieldKey === CONDITIONAL_FIELDS.HF_TOKEN) {
    return selectedTasks.some(task => EVALS_REQUIRING_HF_TOKEN.has(task));
  }

  if (fieldKey === CONDITIONAL_FIELDS.SCORER_API_KEY) {
    return selectedTasks.some(task => EVALS_REQUIRING_SCORER_API_KEY.has(task));
  }

  return true;
}

/**
 * Particular evaluations require additional fields (HF token, scorer API key) in order to run.
 * To reduce confusion for users, unless they've selected a task (eval) that requires these fields,
 * we hide them from the schema.
 */
export function filterSchemasBySelectedTasks(
  schemas: Record<string, any>,
  selectedTasks: string[]
): Record<string, any> {
  if (!schemas['@wandb.config']?.properties) {
    return schemas;
  }

  const properties = pickBy(schemas['@wandb.config'].properties, (value, key) =>
    shouldShowField(key, selectedTasks)
  );

  return {
    ...schemas,
    '@wandb.config': {
      ...schemas['@wandb.config'],
      properties,
    },
  };
}
